import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ApiResponse,
  RoleDownloadResponse,
  RoleFileTypeResponse,
  RoleUIResponse,
} from "../../models/ApiResponse";
import agent from "../../api/agent";
import { RoleFileType, RoleResponse } from "../../models/responseModels/roleResponse";
import {
  AddRoleRequest,
  UpdateRoleRequest,
} from "../../models/requestModels/roleRequest";

type InitialState = {
  isLoading: boolean;
  roleList: RoleResponse[];
  roleFileTypes: RoleFileType[];
  errorMessage: string | null;
};

const initialState: InitialState = {
  isLoading: false,
  roleList: [],
  errorMessage: null,
  roleFileTypes: []
};

export const getRoleList = createAsyncThunk<RoleUIResponse, boolean>(
  "role/roleResponse",
  async (forFdp: boolean) => {
    const response = await agent.RoleAgent.getRoleList(forFdp);
    return response;
  }
);

export const downloadRoleDataToCsv = createAsyncThunk<
  RoleDownloadResponse,
  RoleResponse[]
>("role/downloadCsv", async (roleData: RoleResponse[]) => {
  const response = await agent.RoleAgent.roleCsvDownload(roleData);
  return response;
});

export const downloadRoleDataToXlsx = createAsyncThunk<Blob, RoleResponse[]>(
  "role/downloadXlsx",
  async (roleData: RoleResponse[]) => {
    const response = await agent.RoleAgent.roleXlsxDownload(roleData);
    return response;
  }
);

export const updateRole = createAsyncThunk<ApiResponse, UpdateRoleRequest>(
  "role/updateRole",
  async (updateRoleRequest: UpdateRoleRequest) => {
    const response = await agent.RoleAgent.updateRole(updateRoleRequest);
    return response;
  }
);

export const addRole = createAsyncThunk<ApiResponse, AddRoleRequest>(
  "role/addRole",
  async (addRoleRequest: AddRoleRequest) => {
    const response = await agent.RoleAgent.addRole(addRoleRequest);
    return response;
  }
);

export const fetchFileTypeForRoles = createAsyncThunk<RoleFileTypeResponse, String[]>(
  "allFileTypes/role",
  async (roleNames: String[]) => {
    const response = await agent.RoleAgent.fetchFileTypeForRoles(roleNames);
    return response;
  }
);

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRoleList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRoleList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.roleList = action.payload.data;
        } else {
          state.roleList = [];
        }
      })
      .addCase(getRoleList.rejected, (state, action) => {
        state.isLoading = false;
        state.roleList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(fetchFileTypeForRoles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFileTypeForRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.roleFileTypes = action.payload.data;
        }
        else {
          state.roleFileTypes = [];
        }
      })
      .addCase(fetchFileTypeForRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export default roleSlice.reducer;
